import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import WebsiteCard from '../components/ui/WebsiteCard';
import IPNameModal from '../components/ui/IPNameModal';
import { Website } from '../types';
import { ArrowsUpDownIcon, TableCellsIcon, Squares2X2Icon, CheckCircleIcon, CheckIcon, ArchiveBoxIcon, TrashIcon, PencilIcon, EyeIcon, AdjustmentsHorizontalIcon, TagIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { websiteService, backupService, probeService } from '../services/api';
import { loadIPConfig, IPConfig, getIPName } from '../utils/ipConfig';

// Type pour le tri
type SortField = keyof Website | '';
type SortDirection = 'asc' | 'desc';

// Types pour les colonnes du tableau
type BaseColumnKey = keyof Website | 'actions' | 'httpStatus';
type ExtraColumnKey = 'backupStatus' | 'status' | 'lastUpdated' | 'cookiesManagement' | 'lastBackupDate' | 'mainwpStatus' | 'cloudflareStatus' | 'cookiesStatus';
type ColumnKey = BaseColumnKey | ExtraColumnKey;

interface ColumnConfig {
  key: ColumnKey;
  label: string;
  sortable?: boolean;
  mobileHidden?: boolean;
}

// Configurations prédéfinies de colonnes
const defaultColumnPresets: Record<string, ColumnKey[]> = {
  normal: ['clientName', 'url', 'platform', 'status', 'httpStatus', 'ipAddress', 'backupType', 'emailType', 'lastChecked', 'backupStatus', 'actions'],
  billing: ['clientName', 'url', 'contractType', 'amount', 'lastBillingDate', 'nextBillingDate', 'actions'],
  email: ['clientName', 'url', 'emailType', 'smtpServer', 'smtpPort', 'encryption', 'captchaType', 'actions'],
  domain: ['clientName', 'url', 'domain', 'registrar', 'domainExpiration', 'status', 'actions'],
  maintenance: ['clientName', 'url', 'platform', 'backupType', 'lastBackupDate', 'cookiesManagement', 'cookiesStatus', 'status', 'actions'],
  detection: ['clientName', 'url', 'platform', 'mainwpStatus', 'cloudflareStatus', 'cookiesStatus', 'captchaType', 'actions'],
};

type ColumnPresetKey = keyof typeof defaultColumnPresets;

// Définition de toutes les colonnes disponibles
const allColumns: ColumnConfig[] = [
  { key: 'clientName', label: 'Client', sortable: true },
  { key: 'url', label: 'URL', sortable: true },
  { key: 'platform', label: 'Plateforme', sortable: true, mobileHidden: true },
  { key: 'status', label: 'État du contrat', sortable: true },
  { key: 'httpStatus', label: 'Disponibilité HTTP', sortable: true },
  { key: 'ipAddress', label: 'Adresse IP', sortable: true, mobileHidden: true },
  { key: 'backupStatus', label: 'État Backup', sortable: true },
  { key: 'backupType', label: 'Type de Backup', sortable: true },
  { key: 'lastBackupDate', label: 'Dernier Backup', sortable: true, mobileHidden: true },
  { key: 'lastChecked', label: 'Dernière vérification', sortable: true, mobileHidden: true },
  { key: 'contractType', label: 'Type de contrat', sortable: true, mobileHidden: true },
  { key: 'amount', label: 'Montant', sortable: true, mobileHidden: true },
  { key: 'lastBillingDate', label: 'Dernière facturation', sortable: true, mobileHidden: true },
  { key: 'nextBillingDate', label: 'Prochaine facturation', sortable: true, mobileHidden: true },
  { key: 'emailType', label: 'Type d\'Email', sortable: true, mobileHidden: true },
  { key: 'smtpServer', label: 'Serveur SMTP', sortable: true, mobileHidden: true },
  { key: 'smtpPort', label: 'Port SMTP', sortable: true, mobileHidden: true },
  { key: 'encryption', label: 'Chiffrement', sortable: true, mobileHidden: true },
  { key: 'captchaType', label: 'Captcha', sortable: true, mobileHidden: true },
  { key: 'authType', label: 'Auth (ancien)', sortable: true, mobileHidden: true },
  { key: 'domain', label: 'Domaine', sortable: true, mobileHidden: true },
  { key: 'registrar', label: 'Registrar', sortable: true, mobileHidden: true },
  { key: 'domainExpiration', label: 'Expiration domaine', sortable: true, mobileHidden: true },
  { key: 'lastUpdated', label: 'Dernière mise à jour', sortable: true, mobileHidden: true },
  { key: 'cookiesManagement', label: 'Gestion cookies', sortable: true, mobileHidden: true },
  { key: 'mainwpStatus', label: 'MainWP', sortable: true, mobileHidden: true },
  { key: 'cloudflareStatus', label: 'Cloudflare', sortable: true, mobileHidden: true },
  { key: 'cookiesStatus', label: 'Détection Cookies', sortable: true, mobileHidden: true },
  { key: 'actions', label: 'Actions', sortable: false },
];

// Fonction pour obtenir la configuration d'une colonne par sa clé
const getColumnConfig = (key: ColumnKey): ColumnConfig => {
  return allColumns.find(col => col.key === key) || { key, label: key, sortable: false };
};

// 1. Ajouter la liste des états possibles tout en haut du composant (après les imports)
const CONTRACT_STATES: { value: string; label: string }[] = [
  { value: 'active', label: 'Actif' },
  { value: 'dormant', label: 'En sommeil' },
  { value: 'terminated', label: 'Terminé' },
  { value: 'production', label: 'En production' },
  { value: 'internal', label: 'Projet Interne' },
];

const WebsiteList: React.FC = () => {
  // État pour les sites web
  const [websites, setWebsites] = useState<Website[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<{[key: string]: boolean}>({});
  const [checking, setChecking] = useState<{[key: string]: boolean}>({});
  const [checkingAll, setCheckingAll] = useState(false);
  const [checkingBackup, setCheckingBackup] = useState<{[key: string]: boolean}>({});
  const [checkingAllBackups, setCheckingAllBackups] = useState(false);
  const [detectingCMS, setDetectingCMS] = useState<{[key: string]: boolean}>({});
  const [detectingAllCMS, setDetectingAllCMS] = useState(false);
  const [clearingMainWPCache, setClearingMainWPCache] = useState(false);
  
  // État pour le filtre et la recherche
  const [searchTerm, setSearchTerm] = useState('');
  const [platformFilter, setPlatformFilter] = useState('all');
  const [statusFilters, setStatusFilters] = useState<string[]>(() => {
    const saved = localStorage.getItem('statusFilters');
    return saved ? JSON.parse(saved) : ['all'];
  });
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [httpStatusFilter, setHttpStatusFilter] = useState('all');
  const [backupStatusFilter, setBackupStatusFilter] = useState('all');
  const [backupTypeFilter, setBackupTypeFilter] = useState('all');
  const [ipFilter, setIpFilter] = useState('all');
  const [tagFilter, setTagFilter] = useState('all');
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');
  const [sortField, setSortField] = useState<SortField>('');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  
  // État pour la configuration des colonnes
  const [columnPresets, setColumnPresets] = useState<Record<string, ColumnKey[]>>(() => {
    const savedPresets = localStorage.getItem('columnPresets');
    return savedPresets ? JSON.parse(savedPresets) : defaultColumnPresets;
  });
  
  const [columnPreset, setColumnPreset] = useState<ColumnPresetKey>('normal');
  const [visibleColumns, setVisibleColumns] = useState<ColumnKey[]>(columnPresets.normal);
  const [showColumnSelector, setShowColumnSelector] = useState(false);
  
  // État pour la configuration des IPs
  const [ipConfig, setIpConfig] = useState<IPConfig>({});
  const [isIPModalOpen, setIsIPModalOpen] = useState(false);
  const [currentIP, setCurrentIP] = useState('');
  
  // État pour la modal de confirmation de suppression
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [websiteToDelete, setWebsiteToDelete] = useState<Website | null>(null);
  
  // Sauvegarder les préréglages dans localStorage lorsqu'ils changent
  useEffect(() => {
    localStorage.setItem('columnPresets', JSON.stringify(columnPresets));
  }, [columnPresets]);
  
  // Charger les sites web depuis l'API
  useEffect(() => {
    const fetchWebsites = async () => {
      try {
        setLoading(true);
        const data = await websiteService.getAll();
        setWebsites(data);
        setError(null);
      } catch (err) {
        console.error('Erreur lors du chargement des sites web :', err);
        setError('Impossible de charger les sites web. Veuillez réessayer plus tard.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchWebsites();
  }, []);
  
  // Charger la configuration des IPs depuis le localStorage
  useEffect(() => {
    const config = loadIPConfig();
    setIpConfig(config);
  }, []);
  
  // Mettre à jour les colonnes visibles lorsque le préréglage change
  useEffect(() => {
    setVisibleColumns(columnPresets[columnPreset]);
  }, [columnPreset, columnPresets]);
  
  // Détection du CMS pour un site spécifique
  const handleDetectCMS = async (id: string) => {
    try {
      // Activer l'état de détection
      setDetectingCMS(prev => ({ ...prev, [id]: true }));
      
      console.log(`Démarrage de la détection pour le site ${id}`);
      
      // Lancer toutes les sondes en parallèle
      const [wordpressResult, cloudflareResult, minioResult] = await Promise.all([
        probeService.detectWithProbe(id, 'wordpress'),
        probeService.detectWithProbe(id, 'cloudflare'), 
        probeService.detectWithProbe(id, 'minio')
      ]);
      
      // Log détaillé de la structure complète des résultats
      console.log(`Structure complète des résultats pour ${id}:`, {
        wordpress: wordpressResult.detectionResults,
        cloudflare: cloudflareResult.detectionResults,
        minio: minioResult.detectionResults
      });
      
      // Logs spécifiques pour les sous-sondes importantes
      console.log(`Résultats MainWP pour ${id}:`, 
                 wordpressResult.detectionResults?.subProbes?.mainwp);
      console.log(`Résultats Turnstile pour ${id}:`, 
                 wordpressResult.detectionResults?.subProbes?.turnstile);
      console.log(`Résultats MinIO pour ${id}:`, 
                 minioResult.detectionResults);

      
      // Créer une copie fraîche des sites web
      const updatedWebsites = [...websites];
      
      // Trouver l'index du site dans le tableau
      const siteIndex = updatedWebsites.findIndex(w => w.id === id);
      
      if (siteIndex !== -1) {
        // Combiner tous les résultats de détection
        const combinedDetectionResults = {
          ...updatedWebsites[siteIndex].detectionResults,
          wordpress: wordpressResult.detectionResults,
          cloudflare: cloudflareResult.detectionResults,
          minio: minioResult.detectionResults
        };
        
        // Remplacer le site par une nouvelle référence avec les nouveaux résultats
        updatedWebsites[siteIndex] = {
          ...updatedWebsites[siteIndex],
          detectionResults: combinedDetectionResults
        };
        
        // Mettre à jour l'état avec la nouvelle référence
        setWebsites(updatedWebsites);
        
        console.log(`Site ${id} mis à jour avec les nouveaux résultats de détection (WordPress, Cloudflare, MinIO)`);
        
        // Utiliser un petit délai pour s'assurer que React traite la mise à jour d'état
        setTimeout(() => {
          setDetectingCMS(prev => {
            console.log(`Fin de la détection pour le site ${id} - données mises à jour`);
            return { ...prev, [id]: false };
          });
        }, 300); // Délai plus long pour laisser React traiter la mise à jour
      } else {
        // Si le site n'est pas trouvé, réinitialiser quand même l'état de détection
        setDetectingCMS(prev => ({ ...prev, [id]: false }));
      }
      
    } catch (error) {
      console.error(`Erreur lors de la détection du CMS pour le site ${id}:`, error);
      
      // En cas d'erreur, réinitialiser l'état de détection
      setDetectingCMS(prev => ({ ...prev, [id]: false }));
    }
  };
  
  // Détection du CMS pour tous les sites
  const handleDetectAllCMS = async () => {
    try {
      // Activer l'état de détection globale
      setDetectingAllCMS(true);
      
      console.log("Démarrage de la détection pour tous les sites");
      
      // Lancer toutes les sondes en parallèle pour tous les sites
      const [wordpressResults, cloudflareResults, minioResults] = await Promise.all([
        probeService.detectProbeForAllWebsites('wordpress'),
        probeService.detectProbeForAllWebsites('cloudflare'),
        probeService.detectProbeForAllWebsites('minio')
      ]);
      
      console.log("Résultats reçus pour tous les sites:", {
        wordpress: wordpressResults.length,
        cloudflare: cloudflareResults.length,
        minio: minioResults.length
      });
      
      // Construire un objet pour stocker les résultats par ID de site
      const resultsById: { [id: string]: any } = {};
      
      // Combiner les résultats de toutes les sondes
      wordpressResults.forEach((result: { id: string; detectionResults: any }) => {
        if (!resultsById[result.id]) resultsById[result.id] = {};
        resultsById[result.id].wordpress = result.detectionResults;
      });
      
      cloudflareResults.forEach((result: { id: string; detectionResults: any }) => {
        if (!resultsById[result.id]) resultsById[result.id] = {};
        resultsById[result.id].cloudflare = result.detectionResults;
      });
      
      minioResults.forEach((result: { id: string; detectionResults: any }) => {
        if (!resultsById[result.id]) resultsById[result.id] = {};
        resultsById[result.id].minio = result.detectionResults;
      });
      
      // Créer une copie fraîche des sites web
      const updatedWebsites = websites.map(website => {
        if (resultsById[website.id]) {
          // Combiner les nouveaux résultats avec les existants
          const combinedResults = {
            ...website.detectionResults,
            ...resultsById[website.id]
          };
          
          // Créer une nouvelle référence pour chaque site mis à jour
          return {
            ...website,
            detectionResults: combinedResults
          };
        }
        return website;
      });
      
      // Mettre à jour l'état global avec les nouvelles références
      setWebsites(updatedWebsites);
      
      console.log("Sites mis à jour avec les nouveaux résultats de détection globale (WordPress, Cloudflare, MinIO)");
      
      // Utiliser un délai plus long avant de réinitialiser l'état de détection
      setTimeout(() => {
        console.log("Fin de la détection globale");
        setDetectingAllCMS(false);
      }, 500);
      
    } catch (error) {
      console.error("Erreur lors de la détection du CMS sur tous les sites:", error);
      
      // En cas d'erreur, réinitialiser l'état de détection
      setDetectingAllCMS(false);
    }
  };
  
  // Détection d'une sonde spécifique pour tous les sites
  const handleDetectProbeForAllWebsites = async (probeName: string) => {
    try {
      // Activer l'état de détection globale
      setDetectingAllCMS(true);
      
      console.log(`Démarrage de la détection de ${probeName} pour tous les sites`);
      
      // Détecter la sonde spécifiée sur tous les sites
      const results = await probeService.detectProbeForAllWebsites(probeName);
      
      console.log(`Résultats reçus pour la sonde ${probeName}:`, results.length);
      
      // Construire un objet pour stocker les résultats par ID de site
      const resultsById: { [id: string]: any } = {};
      results.forEach((result: { id: string; detectionResults: any }) => {
        resultsById[result.id] = result.detectionResults;
      });
      
      // Créer une copie fraîche des sites web
      const updatedWebsites = websites.map(website => {
        if (resultsById[website.id]) {
          // Pour la sonde WordPress, on met à jour complètement les résultats WordPress
          if (probeName === 'wordpress') {
            console.log(`Mise à jour des résultats WordPress pour ${website.id}`);
            return {
              ...website,
              detectionResults: {
                ...website.detectionResults,
                wordpress: resultsById[website.id]
              }
            };
          } else {
            // Pour les autres sondes, mise à jour normale
            return {
              ...website,
              detectionResults: {
                ...website.detectionResults,
                [probeName]: resultsById[website.id]
              }
            };
          }
        }
        return website;
      });
      
      // Mettre à jour l'état global avec les nouvelles références
      setWebsites(updatedWebsites);
      
      console.log(`Sites mis à jour avec les résultats de détection pour ${probeName}`);
      
      // Utiliser un délai plus long avant de réinitialiser l'état de détection
      setTimeout(() => {
        console.log(`Fin de la détection de ${probeName}`);
        setDetectingAllCMS(false);
      }, 500);
      
    } catch (error) {
      console.error(`Erreur lors de la détection de ${probeName} sur tous les sites:`, error);
      
      // En cas d'erreur, réinitialiser l'état de détection
      setDetectingAllCMS(false);
    }
  };
  
  // Fonction pour vider le cache MainWP
  const handleClearMainWPCache = async () => {
    try {
      setClearingMainWPCache(true);
      
      console.log('Vidage du cache MainWP...');
      const result = await probeService.clearMainWPCache();
      
      if (result.success) {
        console.log('Cache MainWP vidé avec succès');
        alert('Cache MainWP vidé avec succès. Les prochaines détections utiliseront des données fraîches.');
      } else {
        console.error('Erreur lors du vidage du cache MainWP:', result.message);
        alert(`Erreur lors du vidage du cache MainWP: ${result.message}`);
      }
    } catch (error) {
      console.error('Erreur lors du vidage du cache MainWP:', error);
      alert('Erreur lors du vidage du cache MainWP. Veuillez réessayer plus tard.');
    } finally {
      setClearingMainWPCache(false);
    }
  };
  
  // Fonction pour changer le préréglage de colonnes
  const handlePresetChange = (preset: ColumnPresetKey) => {
    setColumnPreset(preset);
  };
  
  // Fonction pour mettre à jour les colonnes visibles
  const handleColumnToggle = (column: ColumnKey) => {
    const updatedColumns = visibleColumns.includes(column)
      ? visibleColumns.filter(col => col !== column)
      : [...visibleColumns, column];
    
    setVisibleColumns(updatedColumns);
    
    // Mettre à jour le préréglage actuel
    setColumnPresets({
      ...columnPresets,
      [columnPreset]: updatedColumns
    });
  };
  
  // Fonction pour réinitialiser le préréglage actuel aux valeurs par défaut
  const handleResetCurrentPreset = () => {
    const defaultColumns = defaultColumnPresets[columnPreset];
    setVisibleColumns(defaultColumns);
    setColumnPresets({
      ...columnPresets,
      [columnPreset]: defaultColumns
    });
  };
  
  // Fonction pour trier les sites web
  const sortWebsites = (websitesToSort: Website[]) => {
    if (!sortField) return websitesToSort;
    
    return [...websitesToSort].sort((a, b) => {
      // Cas spécial pour lastBackupDate qui se trouve dans backupDetails
      if (sortField === 'lastBackupDate') {
        const aDate = a.backupDetails?.lastBackupDate ? new Date(a.backupDetails.lastBackupDate).getTime() : 0;
        const bDate = b.backupDetails?.lastBackupDate ? new Date(b.backupDetails.lastBackupDate).getTime() : 0;
        
        if (aDate === 0 && bDate === 0) return 0;
        if (aDate === 0) return sortDirection === 'asc' ? 1 : -1;
        if (bDate === 0) return sortDirection === 'asc' ? -1 : 1;
        
        return sortDirection === 'asc' ? aDate - bDate : bDate - aDate;
      }
      
      let aValue = a[sortField] as string | number | boolean;
      let bValue = b[sortField] as string | number | boolean;
      
      // Gérer les cas spéciaux comme les dates, nombres, etc.
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }
      
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };
  
  // Fonction pour gérer le tri
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Changer la direction si on clique sur le même champ
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Nouveau champ, réinitialiser à ascendant
      setSortField(field);
      setSortDirection('asc');
    }
  };
  
  // Fonction pour vérifier le statut d'un site web
  const handleCheckWebsiteStatus = async (id: string) => {
    try {
      setChecking(prev => ({ ...prev, [id]: true }));
      
      // Appel API pour vérifier le statut
      const statusResponse = await websiteService.checkStatus(id);
      
      // Mise à jour du site dans la liste
      setWebsites(prevWebsites => {
        return prevWebsites.map(website => {
          if (website.id === id) {
            return {
              ...website,
              // Mettre à jour seulement httpStatus, pas status qui est réservé pour l'état du contrat
              httpStatus: statusResponse.status, // État HTTP (online/offline/error)
              ipAddress: statusResponse.ipAddress || website.ipAddress, // Mise à jour de l'IP si disponible
              lastChecked: new Date().toISOString(),
              statusDetails: {
                statusCode: statusResponse.statusCode || undefined,
                responseTime: statusResponse.responseTime,
                error: statusResponse.error || undefined,
                htaccessAuthUsed: statusResponse.htaccessAuthUsed || false
              }
            };
          }
          return website;
        });
      });
    } catch (error) {
      console.error('Erreur lors de la vérification du statut:', error);
    } finally {
      setChecking(prev => ({ ...prev, [id]: false }));
    }
  };
  
  // Fonction pour vérifier le statut de tous les sites web
  const handleCheckAllWebsitesStatus = async () => {
    try {
      setCheckingAll(true);
      const statusResponses = await websiteService.checkAllStatus();
      
      // Mettre à jour l'état de tous les sites web dans la liste
      setWebsites(prev => prev.map(website => {
        const statusResponse = statusResponses.find(s => s.id === website.id);
        if (!statusResponse) return website;
        
        return {
          ...website,
          // Mettre à jour seulement httpStatus, pas status qui est réservé pour l'état du contrat
          httpStatus: statusResponse.status, // État HTTP (online/offline/error)
          ipAddress: statusResponse.ipAddress || website.ipAddress, // Mise à jour de l'IP si disponible
          lastChecked: new Date().toISOString(),
          statusDetails: {
            statusCode: statusResponse.statusCode || undefined,
            responseTime: statusResponse.responseTime,
            error: statusResponse.error || undefined,
            htaccessAuthUsed: statusResponse.htaccessAuthUsed || false
          }
        };
      }));
      
      setCheckingAll(false);
    } catch (err) {
      console.error('Erreur lors de la vérification du statut de tous les sites :', err);
      alert('Impossible de vérifier le statut des sites. Veuillez réessayer plus tard.');
      setCheckingAll(false);
    }
  };
  
  // Fonction pour vérifier le backup d'un site web
  const handleCheckWebsiteBackup = async (id: string) => {
    try {
      setCheckingBackup(prev => ({ ...prev, [id]: true }));
      const backupStatus = await backupService.checkWebsiteBackup(id);
      
      // Mettre à jour l'état du site web dans la liste
      setWebsites(prev => prev.map(website => 
        website.id === id 
          ? { 
              ...website, 
              backupStatus: backupStatus.found 
                ? backupStatus.backupDetails?.status.code 
                : 'not_found',
              backupDetails: backupStatus.found && backupStatus.backupDetails 
                ? {
                    bucketName: backupStatus.backupDetails.name,
                    lastBackupDate: backupStatus.backupDetails.latestModification,
                    backupSize: backupStatus.backupDetails.size,
                    objectCount: backupStatus.backupDetails.objectCount
                  } 
                : undefined
            } 
          : website
      ));
      
      setCheckingBackup(prev => ({ ...prev, [id]: false }));
    } catch (err) {
      console.error('Erreur lors de la vérification du backup :', err);
      alert('Impossible de vérifier le backup du site. Veuillez réessayer plus tard.');
      setCheckingBackup(prev => ({ ...prev, [id]: false }));
    }
  };
  
  // Fonction pour vérifier les backups de tous les sites web
  const handleCheckAllWebsitesBackups = async () => {
    try {
      setCheckingAllBackups(true);
      const backupStatuses = await backupService.checkAllBackups();
      
      // Mettre à jour l'état de tous les sites web dans la liste
      setWebsites(prev => prev.map(website => {
        const status = backupStatuses.find(s => s.id === website.id);
        if (!status) return website;
        
        return {
          ...website,
          backupStatus: status.found 
            ? status.backupDetails?.status.code 
            : 'not_found',
          backupDetails: status.found && status.backupDetails 
            ? {
                bucketName: status.backupDetails.name,
                lastBackupDate: status.backupDetails.latestModification,
                backupSize: status.backupDetails.size,
                objectCount: status.backupDetails.objectCount
              } 
            : undefined
        };
      }));
      
      setCheckingAllBackups(false);
    } catch (err) {
      console.error('Erreur lors de la vérification des backups de tous les sites :', err);
      alert('Impossible de vérifier les backups des sites. Veuillez réessayer plus tard.');
      setCheckingAllBackups(false);
    }
  };
  
  // Fonction pour gérer la sélection d'un site
  const handleSelectWebsite = (id: string, selected: boolean) => {
    if (selected) {
      setSelectedIds([...selectedIds, id]);
    } else {
      setSelectedIds(selectedIds.filter(siteId => siteId !== id));
    }
  };
  
  // Fonction pour sélectionner/désélectionner tous les sites
  const handleSelectAll = (selected: boolean) => {
    if (selected) {
      setSelectedIds(filteredWebsites.map(website => website.id));
    } else {
      setSelectedIds([]);
    }
  };
  
  // Fonction pour obtenir toutes les adresses IP distinctes
  const getUniqueIPs = () => {
    const ips = websites
      .filter(website => website.ipAddress)
      .map(website => website.ipAddress);
    
    // Utiliser un objet comme map pour obtenir des valeurs uniques
    const uniqueIpsMap: {[ip: string]: boolean} = {};
    ips.forEach(ip => {
      if (ip) uniqueIpsMap[ip] = true;
    });
    
    // Convertir les clés de l'objet en tableau et trier
    return Object.keys(uniqueIpsMap).sort();
  };
  
  // Fonction pour ouvrir la modale de nommage d'IP
  const handleOpenIPModal = (ip: string) => {
    setCurrentIP(ip);
    setIsIPModalOpen(true);
  };
  
  // Fonction pour gérer la sauvegarde d'un nom d'IP
  const handleSaveIPName = (config: IPConfig) => {
    setIpConfig(config);
  };
  
  // Fonction pour obtenir le nom d'affichage d'une IP
  const getIPDisplayName = (ip: string) => {
    const name = getIPName(ip);
    return name ? `${name} (${ip})` : ip;
  };
  
  // Filtrer les sites en fonction des critères de recherche et de filtrage
  const filteredWebsites = useMemo(() => {
    // Code de débogage - à supprimer en production
    if (process.env.NODE_ENV === 'development') {
      console.log('Status des sites:');
      websites.forEach(website => {
        console.log(`Site: ${website.clientName}, status: ${website.status}, httpStatus: ${website.httpStatus}`);
      });
    }
    
    return websites.filter(website => {
      // Recherche textuelle
      const searchMatch = searchTerm === '' || 
        (website.clientName?.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (website.url?.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (website.ipAddress?.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (website.platform?.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (website.comments?.toLowerCase().includes(searchTerm.toLowerCase()));
      
      // Filtrage par plateforme
      const platformMatch = platformFilter === 'all' || website.platform === platformFilter;
      
      // Filtrage par statut de contrat
      const statusMatch = statusFilters.includes('all') || statusFilters.includes(website.status || '');
      
      // Filtrage par statut HTTP
      const httpStatusMatch = httpStatusFilter === 'all' || website.httpStatus === httpStatusFilter;
      
      // Filtrage par type de backup
      const backupTypeMatch = backupTypeFilter === 'all' || website.backupType === backupTypeFilter;
      
      // Filtrage par statut de backup
      let backupStatusMatch = true;
      if (backupStatusFilter !== 'all') {
        if (backupStatusFilter === 'not_configured') {
          backupStatusMatch = website.backupType === 'aucun';
        } else {
          backupStatusMatch = website.backupStatus === backupStatusFilter;
        }
      }
      
      // Filtrage par tags
      const tagMatch = tagFilter === 'all' || (website.tags && website.tags.includes(tagFilter));
      
      return searchMatch && platformMatch && statusMatch && httpStatusMatch && backupTypeMatch && backupStatusMatch && tagMatch;
    });
  }, [websites, searchTerm, platformFilter, statusFilters, httpStatusFilter, backupTypeFilter, backupStatusFilter, tagFilter]);
  
  // Tri des sites web et s'assurer que les IDs sont uniques
  const sortedWebsites = sortWebsites(filteredWebsites).filter((website, index, self) => 
    index === self.findIndex((w) => w.id === website.id)
  );

  // Rendu des indicateurs de tri
  const renderSortIndicator = (field: SortField) => {
    if (sortField !== field) return null;
    
    return (
      <span className="ml-1 inline-block">
        {sortDirection === 'asc' ? '↑' : '↓'}
      </span>
    );
  };

  // Fonction pour supprimer un site web
  const handleDeleteWebsite = async (id: string) => {
    const website = websites.find(w => w.id === id);
    if (website) {
      setWebsiteToDelete(website);
      setDeleteModalOpen(true);
    }
  };

  // Fonction pour confirmer la suppression
  const confirmDeleteWebsite = async () => {
    if (!websiteToDelete) return;
    
    try {
      setDeleting(prev => ({ ...prev, [websiteToDelete.id]: true }));
      await websiteService.delete(websiteToDelete.id);
      setWebsites(websites.filter(website => website.id !== websiteToDelete.id));
      setSelectedIds(selectedIds.filter(siteId => siteId !== websiteToDelete.id));
      setDeleting(prev => ({ ...prev, [websiteToDelete.id]: false }));
      setDeleteModalOpen(false);
      setWebsiteToDelete(null);
    } catch (err) {
      console.error('Erreur lors de la suppression du site web :', err);
      alert('Impossible de supprimer le site web. Veuillez réessayer plus tard.');
      setDeleting(prev => ({ ...prev, [websiteToDelete.id]: false }));
    }
  };

  // Fonction pour annuler la suppression
  const cancelDeleteWebsite = () => {
    setDeleteModalOpen(false);
    setWebsiteToDelete(null);
  };

  // Fonction pour supprimer plusieurs sites web
  const handleDeleteSelected = async () => {
    if (selectedIds.length === 0) return;
    
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer ${selectedIds.length} sites web ?`)) {
      return;
    }
    
    try {
      const deletePromises = selectedIds.map(id => {
        setDeleting(prev => ({ ...prev, [id]: true }));
        return websiteService.delete(id);
      });
      
      await Promise.all(deletePromises);
      setWebsites(websites.filter(website => !selectedIds.includes(website.id)));
      setSelectedIds([]);
      
      // Réinitialiser l'état de suppression
      const newDeleting = { ...deleting };
      selectedIds.forEach(id => { newDeleting[id] = false; });
      setDeleting(newDeleting);
    } catch (err) {
      console.error('Erreur lors de la suppression des sites web :', err);
      alert('Une erreur est survenue lors de la suppression. Certains sites peuvent ne pas avoir été supprimés.');
      
      // Réinitialiser l'état de suppression
      const newDeleting = { ...deleting };
      selectedIds.forEach(id => { newDeleting[id] = false; });
      setDeleting(newDeleting);
    }
  };

  // Fonction pour déterminer la classe de couleur en fonction de l'âge du backup
  const getBackupDateColorClass = (dateStr: string | undefined): string => {
    if (!dateStr) return 'text-gray-400'; // Non disponible
    
    const backupDate = new Date(dateStr);
    const now = new Date();
    const diffHours = (now.getTime() - backupDate.getTime()) / (1000 * 60 * 60);
    
    if (diffHours < 24) {
      return 'text-green-600 dark:text-green-400 font-medium';
    } else if (diffHours < 72) {
      return 'text-yellow-600 dark:text-yellow-400 font-medium';
    } else {
      return 'text-red-600 dark:text-red-400 font-medium';
    }
  };

  // Afficher un message de chargement
  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  // Afficher un message d'erreur
  if (error) {
    return (
      <div className="bg-red-100 dark:bg-red-900 p-4 rounded-md text-red-800 dark:text-red-200">
        <p>{error}</p>
        <button 
          onClick={() => window.location.reload()} 
          className="mt-2 text-red-700 dark:text-red-300 underline"
        >
          Réessayer
        </button>
      </div>
    );
  }

  // 2. Déclarer handleStatusToggle dans le scope du composant avant le JSX
  const handleStatusToggle = (value: string) => {
    if (value === 'all') {
      // Si on coche "Tous les états", on remplace tout par 'all'
      setStatusFilters(['all']);
    } else {
      let updated: string[];
      if (statusFilters.includes(value)) {
        // Décocher un état spécifique
        updated = statusFilters.filter(v => v !== value && v !== 'all');
      } else {
        // Cocher un état spécifique
        updated = statusFilters.filter(v => v !== 'all').concat(value);
      }
      // Si aucun état n'est sélectionné, revenir à 'all'
      if (updated.length === 0) updated = ['all'];
      setStatusFilters(updated);
    }
  };

  // Rendu du composant
  return (
    <div className="container mx-auto px-4">
      <div className="flex flex-col md:flex-row justify-between items-center mb-6">
        <h1 className="text-2xl font-bold mb-2 md:mb-0">Sites web</h1>
        <div className="flex space-x-2">
          <Link to="/sites/new" className="btn-primary">
            Ajouter un site
          </Link>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg mb-6 p-6">
        <div className="flex flex-col md:flex-row justify-between mb-6 gap-6">
          <div className="flex flex-col md:flex-row gap-5 md:w-2/3">
            <div className="flex-1 relative">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-6 w-6 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Rechercher..."
                className="input-field w-full pl-12"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="w-full md:w-auto relative">
              <button
                id="status-dropdown-btn"
                type="button"
                className="input-field w-[200px] flex items-center justify-between"
                onClick={() => setShowStatusDropdown(v => !v)}
              >
                <span className="truncate">{statusFilters.includes('all') ? 'Tous les états du contrat' : CONTRACT_STATES.filter(s => statusFilters.includes(s.value)).map(s => s.label).join(', ')}</span>
                <svg className="w-4 h-4 ml-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
              </button>
              {showStatusDropdown && (
                <div
                  id="status-dropdown-menu"
                  className="absolute left-0 mt-1 w-64 bg-white dark:bg-gray-800 shadow-lg rounded-md z-50 p-3 border border-gray-200 dark:border-gray-700"
                >
                  <div className="max-h-60 overflow-y-auto">
                    <div className="flex items-center mb-2">
                      <input
                        type="checkbox"
                        id="status-all"
                        checked={statusFilters.includes('all')}
                        onChange={() => handleStatusToggle('all')}
                        className="form-checkbox mr-2"
                      />
                      <label htmlFor="status-all" className="text-sm text-gray-600 dark:text-gray-400">Tous les états du contrat</label>
                    </div>
                    {CONTRACT_STATES.map(state => (
                      <div key={state.value} className="flex items-center mb-1">
                        <input
                          type="checkbox"
                          id={`status-${state.value}`}
                          checked={statusFilters.includes(state.value)}
                          onChange={() => handleStatusToggle(state.value)}
                          className="form-checkbox mr-2"
                        />
                        <label htmlFor={`status-${state.value}`} className="text-sm text-gray-600 dark:text-gray-400">{state.label}</label>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="w-full md:w-auto">
              <select
                className="input-field w-full"
                value={httpStatusFilter}
                onChange={(e) => setHttpStatusFilter(e.target.value)}
              >
                <option value="all">Toutes les disponibilités HTTP</option>
                <option value="online">En ligne</option>
                <option value="offline">Hors ligne</option>
                <option value="error">Erreur</option>
              </select>
            </div>
            <div className="w-full md:w-auto">
              <select
                className="input-field w-full"
                value={tagFilter}
                onChange={(e) => setTagFilter(e.target.value)}
              >
                <option value="all">Tous les tags</option>
                {/* Générer dynamiquement les options de tags à partir des sites existants */}
                {Array.from(new Set(websites.flatMap(website => website.tags || []))).sort().map(tag => (
                  <option key={tag} value={tag}>{tag}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex items-center gap-2 md:w-1/3 justify-end">
            {viewMode === 'table' && (
              <div className="relative">
                <button 
                  onClick={() => setShowColumnSelector(!showColumnSelector)}
                  className="px-3 py-2 rounded bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200"
                  title="Configuration des colonnes"
                >
                  <AdjustmentsHorizontalIcon className="h-5 w-5" />
                </button>
                
                {showColumnSelector && (
                  <div className="absolute top-full right-0 mt-1 w-64 bg-white dark:bg-gray-800 shadow-lg rounded-md z-50 p-3 border border-gray-200 dark:border-gray-700">
                    <div className="mb-2">
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Préréglages</label>
                      <select 
                        className="input-field w-full mt-1"
                        value={columnPreset}
                        onChange={(e) => handlePresetChange(e.target.value as ColumnPresetKey)}
                      >
                        <option value="normal">Normal</option>
                        <option value="billing">Facturation</option>
                        <option value="email">SMTP / Email</option>
                        <option value="domain">Domaine / DNS</option>
                        <option value="maintenance">Maintenance</option>
                      </select>
                    </div>
                    
                    <div className="mb-2">
                      <div className="flex justify-between items-center mb-1">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Colonnes personnalisées</label>
                        <button
                          onClick={handleResetCurrentPreset}
                          className="text-xs text-red-600 dark:text-red-400 hover:underline"
                          title="Réinitialiser ce préréglage"
                        >
                          Réinitialiser
                        </button>
                      </div>
                      <div className="mt-1 max-h-60 overflow-y-auto">
                        {allColumns.map(column => (
                          <div key={column.key as string} className="flex items-center mb-1">
                            <input
                              type="checkbox"
                              id={`column-${column.key}`}
                              checked={visibleColumns.includes(column.key)}
                              onChange={() => handleColumnToggle(column.key)}
                              className="form-checkbox mr-2"
                            />
                            <label 
                              htmlFor={`column-${column.key}`}
                              className="text-sm text-gray-600 dark:text-gray-400"
                            >
                              {column.label}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
            
            <div className="flex">
              <button
                className={`px-3 py-2 rounded-l ${viewMode === 'grid' ? 'bg-primary-500 text-white' : 'bg-gray-200 dark:bg-gray-700'}`}
                onClick={() => setViewMode('grid')}
                title="Vue en grille"
              >
                <Squares2X2Icon className="h-5 w-5" />
              </button>
              <button
                className={`px-3 py-2 rounded-r ${viewMode === 'table' ? 'bg-primary-500 text-white' : 'bg-gray-200 dark:bg-gray-700'}`}
                onClick={() => setViewMode('table')}
                title="Vue en tableau"
              >
                <TableCellsIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>

        <div className="flex flex-col md:flex-row mb-6 gap-5">
          <div className="flex flex-col md:flex-row gap-5 md:w-2/3">
            <div className="w-full md:w-auto">
              <select
                className="input-field w-full"
                value={backupStatusFilter}
                onChange={(e) => setBackupStatusFilter(e.target.value)}
              >
                <option value="all">Tous les statuts de backup</option>
                <option value="recent">Récents</option>
                <option value="warning">À vérifier</option>
                <option value="danger">Anciens</option>
                <option value="not_found">Non trouvés</option>
                <option value="unknown">Non vérifiés</option>
                <option value="not_configured">Non configurés</option>
              </select>
            </div>
            <div className="w-full md:w-auto">
              <div className="flex">
                <select
                  className="input-field w-full rounded-r-none"
                  value={ipFilter}
                  onChange={(e) => setIpFilter(e.target.value)}
                >
                  <option value="all">Toutes les IPs</option>
                  {getUniqueIPs().map(ip => (
                    <option key={ip} value={ip}>
                      {getIPDisplayName(ip)}
                    </option>
                  ))}
                </select>
                <button
                  onClick={() => ipFilter !== 'all' && handleOpenIPModal(ipFilter)}
                  disabled={ipFilter === 'all'}
                  className="px-3 py-2 bg-secondary-600 hover:bg-secondary-700 text-white rounded-r-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed h-10 flex items-center"
                  title="Nommer cette IP"
                >
                  <TagIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
            <div className="w-full md:w-auto">
              <select
                className="input-field w-full"
                value={backupTypeFilter}
                onChange={(e) => setBackupTypeFilter(e.target.value)}
              >
                <option value="all">Tous les types de backup</option>
                <option value="minio">Bucket Minio</option>
                <option value="docker">Serveur Docker</option>
                <option value="autre">Autre</option>
                <option value="aucun">Sans backup</option>
              </select>
            </div>
          </div>
        </div>

        <div className="flex justify-between mb-5">
          <div className="flex gap-3">
            {selectedIds.length > 0 && (
              <button
                onClick={handleDeleteSelected}
                className="btn-danger-sm h-12 flex items-center gap-1 text-base px-4"
                disabled={selectedIds.length === 0}
              >
                <TrashIcon className="h-5 w-5 mr-1" />
                Supprimer ({selectedIds.length})
              </button>
            )}
            {/* Les boutons d'actions ont été déplacés dans le menu déroulant "Actions" du header */}
          </div>
        </div>

        {viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {sortedWebsites.length === 0 ? (
              <div className="col-span-full text-center py-8 text-gray-500 dark:text-gray-400">
                Aucun site web trouvé.
              </div>
            ) : (
              sortedWebsites.map((website) => (
                <WebsiteCard
                  key={website.id}
                  website={website}
                  isSelected={selectedIds.includes(website.id)}
                  onSelect={(selected) => handleSelectWebsite(website.id, selected)}
                  onDelete={handleDeleteWebsite}
                  onCheckStatus={handleCheckWebsiteStatus}
                  onCheckBackup={handleCheckWebsiteBackup}
                  onDetectCMS={handleDetectCMS}
                  isChecking={checking[website.id] || false}
                  isCheckingBackup={checkingBackup[website.id] || false}
                  isDetectingCMS={detectingCMS[website.id] || false}
                  ipConfig={ipConfig}
                  onNameIP={handleOpenIPModal}
                />
              ))
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="text-xs text-gray-700 uppercase bg-gray-100 dark:bg-gray-700 dark:text-gray-300">
                <tr>
                  <th className="px-2 py-4 bg-gray-50 dark:bg-gray-800">
                    <input
                      type="checkbox"
                      checked={selectedIds.length === sortedWebsites.length && sortedWebsites.length > 0}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      className="form-checkbox"
                    />
                  </th>
                  
                  {visibleColumns.map(colKey => {
                    const column = getColumnConfig(colKey);
                    if (colKey === 'actions') return (
                      <th key={colKey} className="px-6 py-3">
                        {column.label}
                      </th>
                    );
                    
                    return (
                      <th 
                        key={colKey}
                        className={`px-6 py-3 cursor-pointer ${column.mobileHidden ? 'hidden md:table-cell' : ''}`}
                        onClick={() => column.sortable ? handleSort(colKey as SortField) : null}
                      >
                        <div className="flex items-center">
                          {column.label}
                          {column.sortable && renderSortIndicator(colKey as SortField)}
                          {column.sortable && <ArrowsUpDownIcon className="ml-1 h-3 w-3" />}
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {sortedWebsites.length === 0 ? (
                  <tr>
                    <td colSpan={visibleColumns.length + 1} className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">
                      Aucun site web trouvé.
                    </td>
                  </tr>
                ) : (
                  sortedWebsites.map((website) => (
                    <tr 
                      key={website.id} 
                      className="border-b dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                    >
                      <td className="px-2 py-4 bg-gray-50 dark:bg-gray-800">
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(website.id)}
                          onChange={(e) => handleSelectWebsite(website.id, e.target.checked)}
                          className="form-checkbox"
                        />
                      </td>
                      
                      {visibleColumns.map(colKey => {
                        const column = getColumnConfig(colKey);
                        
                        if (colKey === 'actions') {
                          return (
                            <td key={colKey} className="px-6 py-4 whitespace-nowrap text-right">
                              <div className="flex justify-end items-center space-x-2">
                                <Link
                                  to={`/sites/${website.id}`}
                                  className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
                                  title="Voir les détails"
                                >
                                  <EyeIcon className="h-4 w-4" />
                                </Link>
                                <Link
                                  to={`/sites/${website.id}/edit`}
                                  className="px-2 py-1 bg-blue-100 dark:bg-blue-900 rounded text-blue-600 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-800"
                                  title="Modifier"
                                >
                                  <PencilIcon className="h-4 w-4" />
                                </Link>
                                <button
                                  onClick={() => handleCheckWebsiteStatus(website.id)}
                                  className="px-2 py-1 bg-green-100 dark:bg-green-900 rounded text-green-600 dark:text-green-300 hover:bg-green-200 dark:hover:bg-green-800"
                                  title="Vérifier le statut"
                                  disabled={checking[website.id]}
                                >
                                  {checking[website.id] ? (
                                    <div className="animate-spin h-4 w-4 border-2 border-green-500 border-t-transparent rounded-full"></div>
                                  ) : (
                                    <CheckIcon className="h-4 w-4" />
                                  )}
                                </button>
                                <button
                                  onClick={() => handleCheckWebsiteBackup(website.id)}
                                  className="px-2 py-1 bg-purple-100 dark:bg-purple-900 rounded text-purple-600 dark:text-purple-300 hover:bg-purple-200 dark:hover:bg-purple-800"
                                  title="Vérifier le backup"
                                  disabled={checkingBackup[website.id]}
                                >
                                  {checkingBackup[website.id] ? (
                                    <div className="animate-spin h-4 w-4 border-2 border-purple-500 border-t-transparent rounded-full"></div>
                                  ) : (
                                    <ArchiveBoxIcon className="h-4 w-4" />
                                  )}
                                </button>
                                <button
                                  onClick={() => handleDetectCMS(website.id)}
                                  className="px-2 py-1 bg-blue-100 dark:bg-blue-900 rounded text-blue-600 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-800"
                                  title="Détecter MainWP"
                                  disabled={detectingCMS[website.id]}
                                >
                                  {detectingCMS[website.id] ? (
                                    <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                                  ) : (
                                    <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                                      <path d="M22 6.2c0-1.7-1.4-3.1-3.1-3.1H5.1C3.4 3.1 2 4.5 2 6.2v11.6c0 1.7 1.4 3.1 3.1 3.1h13.7c1.7 0 3.1-1.4 3.1-3.1V6.2z" />
                                    </svg>
                                  )}
                                </button>
                                <button
                                  onClick={() => handleDeleteWebsite(website.id)}
                                  className="px-2 py-1 bg-red-100 dark:bg-red-900 rounded text-red-600 dark:text-red-300 hover:bg-red-200 dark:hover:bg-red-800"
                                  title="Supprimer"
                                  disabled={deleting[website.id]}
                                >
                                  {deleting[website.id] ? (
                                    <div className="animate-spin h-4 w-4 border-2 border-red-500 border-t-transparent rounded-full"></div>
                                  ) : (
                                    <TrashIcon className="h-4 w-4" />
                                  )}
                                </button>
                              </div>
                            </td>
                          );
                        }
                        
                        let cellContent: React.ReactNode = null;
                        
                        // Rendre le contenu en fonction du type de colonne
                        switch(colKey) {
                          case 'url':
                            cellContent = (
                              <a 
                                href={website.url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-primary-600 dark:text-primary-400 hover:underline"
                              >
                                {website.url}
                              </a>
                            );
                            break;
                            
                          case 'status':
                            cellContent = (
                              <span 
                                className={`px-2 py-1 text-xs font-medium rounded-full ${
                                  website.status === 'active' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                                  website.status === 'dormant' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
                                  website.status === 'terminated' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' :
                                  website.status === 'production' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' :
                                  website.status === 'internal' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200' :
                                  'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200'
                                }`}
                              >
                                {website.status === 'active' ? 'Actif' :
                                website.status === 'dormant' ? 'En sommeil' :
                                website.status === 'terminated' ? 'Terminé' :
                                website.status === 'production' ? 'En production' :
                                website.status === 'internal' ? 'Projet Interne' :
                                website.status || 'Non spécifié'}
                              </span>
                            );
                            break;
                            
                          case 'httpStatus':
                            cellContent = (
                              <span 
                                className={`px-2 py-1 text-xs font-medium rounded-full ${
                                  website.httpStatus === 'online' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                                  website.httpStatus === 'offline' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' :
                                  website.httpStatus === 'error' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200' :
                                  'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200'
                                }`}
                              >
                                {website.httpStatus === 'online' ? 'En ligne' :
                                website.httpStatus === 'offline' ? 'Hors ligne' :
                                website.httpStatus === 'error' ? 'Erreur' :
                                'Non vérifié'}
                              </span>
                            );
                            break;
                            
                          case 'ipAddress':
                            const ipName = website.ipAddress ? getIPName(website.ipAddress) : null;
                            cellContent = (
                              <div className="flex items-center gap-2">
                                <span>{website.ipAddress ? (ipName ? `${ipName} (${website.ipAddress})` : website.ipAddress) : 'Non spécifiée'}</span>
                                {website.ipAddress && (
                                  <button
                                    onClick={() => handleOpenIPModal(website.ipAddress)}
                                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                                    title="Nommer cette IP"
                                  >
                                    <TagIcon className="h-4 w-4" />
                                  </button>
                                )}
                              </div>
                            );
                            break;
                            
                          case 'backupStatus':
                            cellContent = (
                              <span 
                                className={`px-2 py-1 text-xs font-medium rounded-full ${
                                  website.backupStatus === 'recent' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                                  website.backupStatus === 'warning' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
                                  website.backupStatus === 'danger' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' :
                                  website.backupStatus === 'not_found' ? 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200' :
                                  'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200'
                                }`}
                              >
                                {website.backupStatus === 'recent' ? 'Récent' :
                                website.backupStatus === 'warning' ? 'À vérifier' :
                                website.backupStatus === 'danger' ? 'Ancien' :
                                website.backupStatus === 'not_found' ? 'Non trouvé' :
                                'Non vérifié'}
                              </span>
                            );
                            break;
                            
                          case 'backupType':
                            cellContent = (
                              <span 
                                className={`px-2 py-1 text-xs font-medium rounded-full ${
                                  website.backupType === 'minio' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300' :
                                  website.backupType === 'docker' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300' :
                                  website.backupType === 'autre' ? 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-300' :
                                  website.backupType === 'aucun' ? 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300' :
                                  'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300'
                                }`}
                              >
                                {website.backupType === 'minio' ? 'Bucket Minio' :
                                website.backupType === 'docker' ? 'Serveur Docker' :
                                website.backupType === 'autre' ? 'Autre' :
                                website.backupType === 'aucun' ? 'Sans backup' :
                                'Non spécifié'}
                              </span>
                            );
                            break;

                          case 'emailType':
                            cellContent = (
                              <span 
                                className={`px-2 py-1 text-xs font-medium rounded-full ${
                                  website.emailType === 'smtp' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' :
                                  website.emailType === 'postal-smtp' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300' :
                                  website.emailType === 'postal-http' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300' :
                                  website.emailType === 'autre' ? 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-300' :
                                  website.emailType === 'aucun' ? 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300' :
                                  'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300'
                                }`}
                              >
                                {website.emailType === 'smtp' ? 'SMTP' :
                                website.emailType === 'postal-smtp' ? 'Postal SMTP' :
                                website.emailType === 'postal-http' ? 'Postal HTTP' :
                                website.emailType === 'autre' ? 'Autre' :
                                website.emailType === 'aucun' ? 'Sans email' :
                                'Non spécifié'}
                              </span>
                            );
                            break;

                          case 'cookiesManagement':
                            cellContent = (
                              <div className="flex items-center gap-2">
                                <span 
                                  className={`px-2 py-1 text-xs font-medium rounded-full ${
                                    website.cookiesManagement === 'cookieyes' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' :
                                    website.cookiesManagement === 'presendium' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300' :
                                    website.cookiesManagement === 'complianz' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300' :
                                    website.cookiesManagement === 'cookie-notice' ? 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-300' :
                                    website.cookiesManagement === 'gdpr-cookie-compliance' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300' :
                                    website.cookiesManagement === 'iubenda' ? 'bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-300' :
                                    website.cookiesManagement === 'cookie-law-info' ? 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-300' :
                                    website.cookiesManagement === 'non necessaire' ? 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300' :
                                    website.cookiesManagement === 'pas fait' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300' :
                                    'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300'
                                  }`}
                                >
                                  {website.cookiesManagement === 'cookieyes' ? 'CookieYes' :
                                  website.cookiesManagement === 'presendium' ? 'Presendium' :
                                  website.cookiesManagement === 'complianz' ? 'Complianz' :
                                  website.cookiesManagement === 'cookie-notice' ? 'Cookie Notice' :
                                  website.cookiesManagement === 'gdpr-cookie-compliance' ? 'GDPR Cookie Compliance' :
                                  website.cookiesManagement === 'iubenda' ? 'Iubenda' :
                                  website.cookiesManagement === 'cookie-law-info' ? 'Cookie Law Info' :
                                  website.cookiesManagement === 'non necessaire' ? 'Non nécessaire' :
                                  website.cookiesManagement === 'pas fait' ? 'Pas fait' :
                                  website.cookiesManagement || 'Non spécifié'}
                                </span>
                                
                                {/* Afficher l'icône de détection cookies si disponible */}
                                {(() => {
                                  const cookiesData = website.detectionResults?.wordpress?.subProbes?.cookies;
                                  const cookiesStatus = cookiesData?.status;
                                  
                                  if (!cookiesData || !cookiesStatus) {
                                    return null;
                                  }
                                  
                                  let iconSrc = '';
                                  let altText = '';
                                  let titleText = '';
                                  
                                  switch (cookiesStatus) {
                                    case 'match':
                                      iconSrc = '/icons/cookies-icon-green.svg';
                                      altText = 'Plugin cookies conforme';
                                      titleText = 'Plugin détecté correspond à la configuration';
                                      break;
                                    case 'mismatch':
                                      iconSrc = '/icons/cookies-icon-orange.svg';
                                      altText = 'Plugin cookies différent';
                                      titleText = 'Plugin détecté différent de la configuration';
                                      break;
                                    case 'missing':
                                      iconSrc = '/icons/cookies-icon-red.svg';
                                      altText = 'Plugin cookies manquant';
                                      titleText = 'Aucun plugin détecté mais gestionnaire configuré';
                                      break;
                                    case 'unspecified':
                                      iconSrc = '/icons/cookies-icon-gray.svg';
                                      altText = 'Plugin cookies non spécifié';
                                      titleText = 'Plugin détecté mais gestionnaire non spécifié';
                                      break;
                                    default:
                                      return null;
                                  }
                                  
                                  return (
                                    <img 
                                      src={iconSrc} 
                                      alt={altText} 
                                      className="w-4 h-4"
                                      title={titleText}
                                    />
                                  );
                                })()}
                              </div>
                            );
                            break;

                          case 'lastBackupDate':
                            cellContent = website.backupDetails && website.backupDetails.lastBackupDate
                              ? <span className={getBackupDateColorClass(website.backupDetails.lastBackupDate)}>
                                  {new Date(website.backupDetails.lastBackupDate).toLocaleDateString()}
                                </span>
                              : <span className="text-gray-400">Non disponible</span>;
                            break;
                            
                          case 'lastChecked':
                          case 'lastBillingDate':
                          case 'nextBillingDate':
                          case 'domainExpiration':
                            const date = website[colKey] as string;
                            cellContent = date 
                              ? new Date(date).toLocaleDateString() 
                              : <span className="text-gray-400">Non spécifié</span>;
                            break;
                          
                          case 'lastUpdated':
                            cellContent = <span className="text-gray-400">Non disponible</span>;
                            break;

                          case 'captchaType':
                            cellContent = (
                              <div className="flex items-center gap-2">
                                <span>
                                  {website.captchaType || website.authType || 'Non spécifié'}
                                </span>
                                {/* Afficher l'icône Turnstile selon la logique demandée */}
                                {website.captchaType === 'Turnstile' && (() => {
                                  const turnstileDetected = website.detectionResults?.wordpress?.subProbes?.turnstile?.detected === true;
                                  const isConfigured = website.detectionResults?.wordpress?.subProbes?.turnstile?.isConfigured === true || 
                                                     website.detectionResults?.wordpress?.subProbes?.turnstile?.isConfigured === 1;
                                  
                                  // Chercher le plugin Turnstile dans la liste des plugins MainWP
                                  const mainwpPlugins = website.detectionResults?.wordpress?.subProbes?.mainwp?.plugins || [];
                                  const turnstilePlugin = mainwpPlugins.find((plugin: any) => 
                                    plugin.slug && plugin.slug.includes('turnstile')
                                  );
                                  
                                  // Si on a des données MainWP, utiliser le statut MainWP
                                  if (turnstilePlugin) {
                                    const turnstileActive = turnstilePlugin.active === 1;
                                    // Si le plugin est détecté ET actif ET configuré → icône verte
                                    if (turnstileDetected && turnstileActive && isConfigured) {
                                      return (
                                        <img 
                                          src="/icons/turnstile-icon-green.svg" 
                                          alt="Turnstile actif" 
                                          className="w-4 h-4"
                                          aria-label="Protection Turnstile active et configurée"
                                          title="Turnstile est correctement installé, activé et configuré"
                                        />
                                      );
                                    }
                                    // Si captchaType=Turnstile mais plugin désactivé/absent/non configuré → icône rouge
                                    else {
                                      return (
                                        <img 
                                          src="/icons/turnstile-icon-red.svg" 
                                          alt="Turnstile inactif" 
                                          className="w-4 h-4"
                                          aria-label="Protection Turnstile désactivée ou non configurée"
                                          title="Turnstile est configuré mais le plugin est désactivé ou non configuré"
                                        />
                                      );
                                    }
                                  }
                                  // Si pas de données MainWP, se baser uniquement sur la détection HTML/JS
                                  else {
                                    // Si le plugin est détecté via HTML/JS → icône verte
                                    if (turnstileDetected) {
                                      return (
                                        <img 
                                          src="/icons/turnstile-icon-green.svg" 
                                          alt="Turnstile actif" 
                                          className="w-4 h-4"
                                          aria-label="Protection Turnstile active et configurée"
                                          title="Turnstile détecté et actif"
                                        />
                                      );
                                    }
                                    // Si captchaType=Turnstile mais plugin non détecté → icône rouge
                                    else {
                                      return (
                                        <img 
                                          src="/icons/turnstile-icon-red.svg" 
                                          alt="Turnstile inactif" 
                                          className="w-4 h-4"
                                          aria-label="Protection Turnstile désactivée ou non configurée"
                                          title="Turnstile configuré mais non détecté"
                                        />
                                      );
                                    }
                                  }
                                })()}
                              </div>
                            );
                            break;

                          case 'mainwpStatus':
                            cellContent = (
                              <div className="flex items-center gap-2">
                                {(() => {
                                  const wordpressDetected = website.detectionResults?.wordpress?.detected === true;
                                  const mainwpData = website.detectionResults?.wordpress?.subProbes?.mainwp;
                                  const hasMainwpData = mainwpData !== undefined;
                                  const mainwpDetected = mainwpData?.detected === true;
                                  
                                  if (!wordpressDetected) {
                                    return <span className="text-gray-400">Non WordPress</span>;
                                  }
                                  
                                  if (!hasMainwpData) {
                                    return <span className="text-gray-400">Non vérifié</span>;
                                  }
                                  
                                  if (mainwpDetected) {
                                    return (
                                      <div className="flex items-center gap-1">
                                        <img 
                                          src="/icons/mainwp-icon-green.svg" 
                                          alt="MainWP détecté" 
                                          className="w-4 h-4"
                                          title="Site géré par MainWP"
                                        />
                                        <span className="text-green-600 dark:text-green-400">Géré</span>
                                      </div>
                                    );
                                  } else {
                                    return (
                                      <div className="flex items-center gap-1">
                                        <img 
                                          src="/icons/mainwp-icon-red.svg" 
                                          alt="MainWP non détecté" 
                                          className="w-4 h-4"
                                          title="Site WordPress non géré par MainWP"
                                        />
                                        <span className="text-red-600 dark:text-red-400">Non géré</span>
                                      </div>
                                    );
                                  }
                                })()}
                              </div>
                            );
                            break;

                          case 'cloudflareStatus':
                            cellContent = (
                              <div className="flex items-center gap-2">
                                {(() => {
                                  const cloudflareDetected = website.detectionResults?.cloudflare?.detected === true;
                                  const hasCloudflareData = website.detectionResults?.cloudflare !== undefined;
                                  
                                  if (!hasCloudflareData) {
                                    return <span className="text-gray-400">Non vérifié</span>;
                                  }
                                  
                                  if (cloudflareDetected) {
                                    return (
                                      <div className="flex items-center gap-1">
                                        <img 
                                          src="/icons/cloudflare.svg" 
                                          alt="Protégé par Cloudflare" 
                                          className="w-4 h-4"
                                          title="Site protégé par Cloudflare"
                                        />
                                        <span className="text-orange-600 dark:text-orange-400">Protégé</span>
                                      </div>
                                    );
                                  } else {
                                    return <span className="text-gray-600 dark:text-gray-400">Non protégé</span>;
                                  }
                                })()}
                              </div>
                            );
                            break;

                          case 'cookiesStatus':
                            cellContent = (
                              <div className="flex items-center gap-2">
                                {(() => {
                                  const cookiesData = website.detectionResults?.wordpress?.subProbes?.cookies;
                                  const hasCookiesData = cookiesData !== undefined;
                                  const cookiesDetected = cookiesData?.detected === true;
                                  const detectedPlugin = cookiesData?.detectedPlugin;
                                  const configuredPlugin = website.cookiesManagement;
                                  const cookiesStatus = cookiesData?.status;
                                  
                                  if (!hasCookiesData) {
                                    return <span className="text-gray-400">Non vérifié</span>;
                                  }
                                  
                                  if (!cookiesDetected) {
                                    if (configuredPlugin && configuredPlugin !== 'non necessaire' && configuredPlugin !== 'pas fait') {
                                      return (
                                        <div className="flex items-center gap-1">
                                          <img 
                                            src="/icons/cookies-icon-red.svg" 
                                            alt="Plugin cookies manquant" 
                                            className="w-4 h-4"
                                            title="Aucun plugin détecté mais gestionnaire configuré"
                                          />
                                          <span className="text-red-600 dark:text-red-400">Manquant</span>
                                        </div>
                                      );
                                    } else {
                                      return <span className="text-gray-600 dark:text-gray-400">Aucun plugin</span>;
                                    }
                                  }
                                  
                                  // Plugin détecté
                                  let iconSrc = '';
                                  let statusText = '';
                                  let statusColor = '';
                                  
                                  switch (cookiesStatus) {
                                    case 'match':
                                      iconSrc = '/icons/cookies-icon-green.svg';
                                      statusText = 'Conforme';
                                      statusColor = 'text-green-600 dark:text-green-400';
                                      break;
                                    case 'mismatch':
                                      iconSrc = '/icons/cookies-icon-orange.svg';
                                      statusText = 'Différent';
                                      statusColor = 'text-orange-600 dark:text-orange-400';
                                      break;
                                    case 'missing':
                                      iconSrc = '/icons/cookies-icon-red.svg';
                                      statusText = 'Manquant';
                                      statusColor = 'text-red-600 dark:text-red-400';
                                      break;
                                    case 'unspecified':
                                      iconSrc = '/icons/cookies-icon-gray.svg';
                                      statusText = 'Non spécifié';
                                      statusColor = 'text-gray-600 dark:text-gray-400';
                                      break;
                                    default:
                                      iconSrc = '/icons/cookies-icon-gray.svg';
                                      statusText = 'Détecté';
                                      statusColor = 'text-gray-600 dark:text-gray-400';
                                  }
                                  
                                  return (
                                    <div className="flex items-center gap-1">
                                      <img 
                                        src={iconSrc} 
                                        alt={`Plugin cookies ${statusText.toLowerCase()}`} 
                                        className="w-4 h-4"
                                        title={`${detectedPlugin?.name || 'Plugin détecté'} - ${statusText}`}
                                      />
                                      <span className={statusColor}>{statusText}</span>
                                    </div>
                                  );
                                })()}
                              </div>
                            );
                            break;

                          case 'amount':
                            cellContent = website.amount 
                              ? `${website.amount} €` 
                              : <span className="text-gray-400">Non spécifié</span>;
                            break;
                            
                          default:
                            // Pour toutes les autres colonnes textuelles
                            cellContent = (
                              <span>
                                {typeof website[colKey as keyof Website] === 'string' || 
                                 typeof website[colKey as keyof Website] === 'number' 
                                  ? String(website[colKey as keyof Website]) 
                                  : <span className="text-gray-400">Non spécifié</span>}
                              </span>
                            );
                        }
                        
                        return (
                          <td 
                            key={colKey} 
                            className={`px-6 py-4 whitespace-nowrap ${column.mobileHidden ? 'hidden md:table-cell' : ''}`}
                          >
                            {cellContent}
                          </td>
                        );
                      })}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-4 text-sm text-gray-500 dark:text-gray-400">
          {sortedWebsites.length} site(s) affiché(s) sur {websites.length} total
        </div>
      </div>
      
      {/* Modal pour nommer les IPs */}
      <IPNameModal
        ip={currentIP}
        isOpen={isIPModalOpen}
        onClose={() => setIsIPModalOpen(false)}
        onSave={handleSaveIPName}
        initialName={ipConfig[currentIP]?.name || ''}
        initialDescription={ipConfig[currentIP]?.description || ''}
        initialColor={ipConfig[currentIP]?.color || ''}
      />
      
      {/* Modal de confirmation de suppression */}
      {deleteModalOpen && websiteToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  Confirmer la suppression
                </h3>
              </div>
            </div>
            
            <div className="mb-6">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Êtes-vous sûr de vouloir supprimer le site web suivant ? Cette action est irréversible.
              </p>
              <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-md">
                <p className="font-medium text-gray-900 dark:text-white">{websiteToDelete.clientName}</p>
                <p className="text-sm text-gray-600 dark:text-gray-300">{websiteToDelete.url}</p>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={cancelDeleteWebsite}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 hover:bg-gray-200 dark:hover:bg-gray-500 rounded-md transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={confirmDeleteWebsite}
                disabled={deleting[websiteToDelete.id]}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-md transition-colors flex items-center"
              >
                {deleting[websiteToDelete.id] ? (
                  <>
                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                    Suppression...
                  </>
                ) : (
                  'Supprimer'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WebsiteList; 